import {
  ArrowLeft,
  Clock,
  Plus,
  Settings,
  Trash2,
  Users,
  X,
} from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import Card from "../components/Card"
import ProjectTaskKanban from "../components/ProjectTaskKanban"
import StatusPill from "../components/StatusPill"
import TaskDetailModal from "../components/TaskDetailModal"
import { cx } from "../lib/cx"
import type { Project, ProjectTask, Task } from "../types"

type ProjectDetail = Project & {
  tasks: ProjectTask[]
  timeEntries: Array<{
    id: string
    userId: string
    userName: string
    taskId: string | null
    date: string
    durationHours: number
    note: string
    createdAt: string
  }>
  assignments: Array<{
    userId: string
    userName: string
    email: string
    role: string
    userRole: string
  }>
}

type Tab = "tasks" | "time" | "team" | "settings"

const statusOptions: Array<{ value: Project["status"]; label: string }> = [
  { value: "Planned", label: "Planned" },
  { value: "Active", label: "Active" },
  { value: "Review", label: "Review" },
  { value: "Complete", label: "Complete" },
]

const marginTone = (s: string): "success" | "warning" | "danger" | "neutral" => {
  if (s === "hot") return "danger"
  if (s === "growing") return "success"
  return "neutral"
}

const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<Tab>("tasks")
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [allUsers, setAllUsers] = useState<Array<{ id: string; name: string; email: string }>>([])

  // Time log form
  const [timeForm, setTimeForm] = useState({ duration: "", date: "", note: "", taskId: "" })
  const [showTimeForm, setShowTimeForm] = useState(false)

  // Settings form
  const [editForm, setEditForm] = useState<Record<string, string>>({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const fetchProject = useCallback(async () => {
    try {
      const res = await fetch(`/api/projects/${id}`)
      if (!res.ok) { navigate("/projects"); return }
      const data = await res.json()
      setProject(data.project)
      setEditForm({
        name: data.project.name || "",
        description: data.project.description || "",
        budgetHours: String(data.project.budgetHours || 0),
        hourlyRate: String(data.project.hourlyRate || 0),
        dueDate: data.project.dueDate || "",
      })
    } catch {
      navigate("/projects")
    } finally {
      setIsLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    void fetchProject()
    fetch("/api/users").then(r => r.json()).then(d => {
      if (d.users) setAllUsers(d.users)
    }).catch(() => {})
  }, [fetchProject])

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-stone">Loading project...</div>
  }

  if (!project) return null

  const handleStatusChange = async (newStatus: Project["status"]) => {
    const prev = project.status
    setProject({ ...project, status: newStatus })
    try {
      const res = await fetch(`/api/projects/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      })
      if (!res.ok) throw new Error()
    } catch {
      setProject((p) => p ? { ...p, status: prev } : p)
    }
  }

  const handleTaskStatusChange = async (taskId: string, newStatus: ProjectTask["status"]) => {
    setProject((p) => {
      if (!p) return p
      return {
        ...p,
        tasks: p.tasks.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)),
        tasksDone: p.tasks.filter((t) => (t.id === taskId ? newStatus : t.status) === "done").length,
      }
    })
    try {
      await fetch(`/api/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      })
    } catch { /* revert on next fetch */ }
  }

  const handleTaskClick = (taskId: string) => {
    const task = project.tasks.find((t) => t.id === taskId)
    if (task) {
      const taskWithTags = task as ProjectTask & { tags?: string[] }
      setSelectedTask({
        ...task,
        projectId: project.id,
        project: project.name,
        clientId: project.clientId,
        client: project.client,
        priority: (task.priority || "medium") as Task["priority"],
        tags: taskWithTags.tags || [],
      } as Task)
      setIsTaskModalOpen(true)
    }
  }

  const handleTaskUpdate = async (taskId: string, updates: Partial<Task>) => {
    setProject((p) => {
      if (!p) return p
      return {
        ...p,
        tasks: p.tasks.map((t) =>
          t.id === taskId ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t,
        ),
      }
    })
    if (selectedTask?.id === taskId) {
      setSelectedTask((t) => t ? { ...t, ...updates } : t)
    }
    try {
      await fetch(`/api/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      })
    } catch { /* revert on next fetch */ }
  }

  const handleTaskDelete = async (taskId: string) => {
    setProject((p) => {
      if (!p) return p
      return {
        ...p,
        tasks: p.tasks.filter((t) => t.id !== taskId),
        tasksTotal: p.tasksTotal - 1,
      }
    })
    setIsTaskModalOpen(false)
    setSelectedTask(null)
    try {
      await fetch(`/api/tasks/${taskId}`, { method: "DELETE" })
    } catch { /* ok */ }
  }

  const handleAddTask = async (title: string) => {
    const tempId = `task-${Date.now()}`
    const newTask: ProjectTask = {
      id: tempId,
      title,
      status: "todo",
      priority: "medium",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setProject((p) => p ? { ...p, tasks: [...p.tasks, newTask], tasksTotal: p.tasksTotal + 1 } : p)

    try {
      const res = await fetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, projectId: id, status: "todo", priority: "medium" }),
      })
      if (res.ok) {
        const data = await res.json()
        setProject((p) => p ? {
          ...p,
          tasks: p.tasks.map((t) => (t.id === tempId ? { ...t, id: data.id } : t)),
        } : p)
      } else {
        throw new Error("Failed")
      }
    } catch {
      setProject((p) =>
        p
          ? {
              ...p,
              tasks: p.tasks.filter((t) => t.id !== tempId),
              tasksTotal: Math.max(0, p.tasksTotal - 1),
            }
          : p,
      )
    }
  }

  const handleLogTime = async () => {
    const duration = parseFloat(timeForm.duration)
    if (!duration || duration <= 0) return

    try {
      await fetch("/api/time-entries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: id,
          task_id: timeForm.taskId || null,
          duration,
          date: timeForm.date || undefined,
          note: timeForm.note,
        }),
      })
      setTimeForm({ duration: "", date: "", note: "", taskId: "" })
      setShowTimeForm(false)
      fetchProject()
    } catch { /* ok */ }
  }

  const handleAddMember = async (userId: string) => {
    try {
      await fetch(`/api/projects/${id}/assignments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      })
      fetchProject()
    } catch { /* ok */ }
  }

  const handleRemoveMember = async (userId: string) => {
    try {
      await fetch(`/api/projects/${id}/assignments?userId=${userId}`, { method: "DELETE" })
      fetchProject()
    } catch { /* ok */ }
  }

  const handleSaveSettings = async () => {
    try {
      await fetch(`/api/projects/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editForm.name,
          description: editForm.description,
          budgetHours: parseFloat(editForm.budgetHours) || 0,
          hourlyRate: parseFloat(editForm.hourlyRate) || 0,
          dueDate: editForm.dueDate || null,
        }),
      })
      fetchProject()
    } catch { /* ok */ }
  }

  const handleDelete = async () => {
    try {
      await fetch(`/api/projects/${id}`, { method: "DELETE" })
      navigate("/projects")
    } catch { /* ok */ }
  }

  const pct = project.budgetHours > 0 ? (project.hoursUsed / project.budgetHours) * 100 : 0

  const tabs: Array<{ id: Tab; label: string; icon: React.ReactNode }> = [
    { id: "tasks", label: "Tasks", icon: null },
    { id: "time", label: "Time", icon: <Clock className="h-4 w-4" /> },
    { id: "team", label: "Team", icon: <Users className="h-4 w-4" /> },
    { id: "settings", label: "Settings", icon: <Settings className="h-4 w-4" /> },
  ]

  const assignedUserIds = new Set(project.assignments.map((a) => a.userId))
  const availableUsers = allUsers.filter((u) => !assignedUserIds.has(u.id))

  return (
    <>
      <div className="space-y-6">
        {/* Back + Header */}
        <Card>
          <button
            type="button"
            onClick={() => navigate("/projects")}
            className="mb-4 flex items-center gap-1 text-sm text-stone hover:text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Projects
          </button>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-slate-900">{project.name}</h1>
              <p className="mt-1 text-sm text-stone">{project.client}</p>
              {project.description && (
                <p className="mt-2 text-sm text-slate-700">{project.description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <select
                value={project.status}
                onChange={(e) => handleStatusChange(e.target.value as Project["status"])}
                className="rounded-lg border border-line bg-white px-3 py-2 text-sm font-medium outline-none focus:border-moss"
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div className="text-center">
              <p className="text-lg font-semibold text-slate-900">
                {project.hoursUsed}/{project.budgetHours}h
              </p>
              <p className="text-xs text-stone">Budget</p>
            </div>
            <div className="text-center">
              <StatusPill tone={marginTone(project.marginSignal)} label={
                project.marginSignal === "hot" ? "Over budget" :
                project.marginSignal === "growing" ? "Under budget" : "On track"
              } />
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-slate-900">
                {project.tasksDone}/{project.tasksTotal}
              </p>
              <p className="text-xs text-stone">Tasks done</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-slate-700">
                {project.dueDate ? new Date(project.dueDate).toLocaleDateString() : "No due date"}
              </p>
              <p className="text-xs text-stone">Due date</p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cx("h-full transition-all", pct > 90 ? "bg-rose-500" : "bg-moss")}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <p className="mt-1 text-center text-xs text-stone">{Math.round(pct)}% budget used</p>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-line">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cx(
                "flex items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-medium transition",
                activeTab === tab.id
                  ? "border-moss text-moss"
                  : "border-transparent text-stone hover:text-slate-700",
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "tasks" && (
          <ProjectTaskKanban
            tasks={project.tasks}
            onStatusChange={handleTaskStatusChange}
            onTaskClick={handleTaskClick}
            onAddTask={handleAddTask}
          />
        )}

        {activeTab === "time" && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Time Entries</h2>
              <button
                type="button"
                onClick={() => setShowTimeForm(!showTimeForm)}
                className="flex items-center gap-2 rounded-lg bg-moss px-3 py-2 text-sm font-medium text-white hover:bg-moss/90"
              >
                <Plus className="h-4 w-4" /> Log Time
              </button>
            </div>

            {showTimeForm && (
              <div className="mb-4 rounded-lg border border-line bg-slate-50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">Hours</label>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      value={timeForm.duration}
                      onChange={(e) => setTimeForm({ ...timeForm, duration: e.target.value })}
                      className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                      placeholder="1.5"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">Date</label>
                    <input
                      type="date"
                      value={timeForm.date}
                      onChange={(e) => setTimeForm({ ...timeForm, date: e.target.value })}
                      className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">Task</label>
                    <select
                      value={timeForm.taskId}
                      onChange={(e) => setTimeForm({ ...timeForm, taskId: e.target.value })}
                      className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    >
                      <option value="">No task</option>
                      {project.tasks.map((t) => (
                        <option key={t.id} value={t.id}>{t.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">Note</label>
                    <input
                      type="text"
                      value={timeForm.note}
                      onChange={(e) => setTimeForm({ ...timeForm, note: e.target.value })}
                      className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                      placeholder="What did you work on?"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleLogTime}
                    className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTimeForm(false)}
                    className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {project.timeEntries.length > 0 ? (
              <div className="space-y-2">
                {project.timeEntries.map((te) => (
                  <div key={te.id} className="flex items-center justify-between rounded-lg border border-line bg-white p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-moss/10 text-xs font-medium text-moss">
                        {te.userName?.[0] || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{te.userName}</p>
                        <p className="text-xs text-stone">{te.note || "No note"}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">{te.durationHours.toFixed(1)}h</p>
                      <p className="text-xs text-stone">{te.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-stone">No time entries yet</p>
            )}
          </Card>
        )}

        {activeTab === "team" && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Team Members</h2>
              {availableUsers.length > 0 && (
                <div className="relative">
                  <select
                    onChange={(e) => {
                      if (e.target.value) handleAddMember(e.target.value)
                      e.target.value = ""
                    }}
                    className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    defaultValue=""
                  >
                    <option value="" disabled>Add member...</option>
                    {availableUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {project.assignments.length > 0 ? (
              <div className="space-y-2">
                {project.assignments.map((a) => (
                  <div key={a.userId} className="flex items-center justify-between rounded-lg border border-line bg-white p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-moss/10 text-xs font-medium text-moss">
                        {a.userName?.[0] || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{a.userName}</p>
                        <p className="text-xs text-stone">{a.email} · {a.userRole}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(a.userId)}
                      className="flex h-7 w-7 items-center justify-center rounded text-stone hover:bg-rose-50 hover:text-rose-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-stone">No team members assigned</p>
            )}
          </Card>
        )}

        {activeTab === "settings" && (
          <Card>
            <h2 className="mb-4 text-lg font-semibold text-slate-900">Project Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">Project Name</label>
                <input
                  type="text"
                  value={editForm.name || ""}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">Description</label>
                <textarea
                  value={editForm.description || ""}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full resize-none rounded-lg border border-line bg-white p-3 text-sm outline-none focus:border-moss"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">Budget Hours</label>
                  <input
                    type="number"
                    value={editForm.budgetHours || ""}
                    onChange={(e) => setEditForm({ ...editForm, budgetHours: e.target.value })}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    min="0"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">Hourly Rate ($)</label>
                  <input
                    type="number"
                    value={editForm.hourlyRate || ""}
                    onChange={(e) => setEditForm({ ...editForm, hourlyRate: e.target.value })}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    min="0"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">Due Date</label>
                  <input
                    type="date"
                    value={editForm.dueDate ? editForm.dueDate.split("T")[0] : ""}
                    onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-line pt-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="h-4 w-4" /> Delete Project
                </button>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
                >
                  Save Changes
                </button>
              </div>
            </div>

            {/* Delete confirmation */}
            {showDeleteConfirm && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-4">
                <p className="text-sm font-medium text-rose-800">
                  Are you sure? This will delete all tasks, time entries, and assignments for this project.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
                  >
                    Yes, Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isTaskModalOpen}
        onClose={() => { setIsTaskModalOpen(false); setSelectedTask(null) }}
        onUpdate={handleTaskUpdate}
        onDelete={handleTaskDelete}
      />
    </>
  )
}

export default ProjectDetailPage
